import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';

import Animation from '@adsk/alloy-react-animation';
import { useMeasure, useForwardedRef } from '@adsk/alloy-react-helpers';
import { stylePropType } from '@adsk/alloy-react-theme';

/**
 * Get the duration of the animation depending upon
 * the height provided.
 * @param {number} height of container
 */

function getAutoHeightDuration(height?: number): number {
  if (!height) return 0;
  const constant = height / 36;
  return Math.round((4 + 15 * constant ** 0.25 + constant / 5) * 10) / 1000;
}

type AnimateHeightProps = Omit<
  ComponentProps<typeof Animation>,
  'children' | 'as'
> & {
  timeout?: number;
  isOpen: boolean;
  height?: number;
  onEntered?: () => void;
  onExited?: () => void;
  children?: ((state: 'entered' | 'exited') => ReactNode) | ReactNode;
  as?: React.ElementType;
};

const AnimateHeight = React.forwardRef<HTMLDivElement, AnimateHeightProps>(
  function (
    {
      style,
      className,
      timeout,
      isOpen,
      children,
      height = 0,
      onEntered,
      onExited,
      onAnimationStart,
      onAnimationComplete,
      as: asProp,
      ...rest
    },
    _ref,
  ) {
    const ref = useForwardedRef(_ref);
    const bounds = useMeasure(ref);
    const heightByState = isOpen ? 'auto' : height;
    const motionProps = { height: heightByState };
    const matrixState = isOpen ? 'entered' : 'exited';

    const Component = asProp || 'div';

    const onAnimationCompleteWrapper = () => {
      if (onAnimationComplete) {
        onAnimationComplete();
      }

      if (onEntered && isOpen) {
        onEntered();
      }

      if (onExited && !isOpen) {
        onExited();
      }
    };

    return (
      <Animation
        style={[{ overflow: 'hidden' }, style]}
        className={className}
        initial={motionProps}
        animate={motionProps}
        transition={{
          ease: 'easeOut',
          duration:
            typeof timeout === 'number'
              ? timeout
              : getAutoHeightDuration(bounds.height),
        }}
        onAnimationStart={onAnimationStart}
        onAnimationComplete={onAnimationCompleteWrapper}
        {...rest}
      >
        <Component ref={ref}>
          {typeof children === 'function' ? children(matrixState) : children}
        </Component>
      </Animation>
    );
  },
);

AnimateHeight.displayName = 'AnimateHeight';

AnimateHeight.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The content to animate */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Animation state */
  isOpen: PropTypes.bool.isRequired,
  /** Height animation timeout */
  timeout: PropTypes.number,
  /** Callback when exit animation is complete */
  onExited: PropTypes.func,
  /** Callback when enter animation is complete */
  onEntered: PropTypes.func,
  /** Callback when animation has started */
  onAnimationStart: PropTypes.func,
  /** Callback when animation is complete */
  onAnimationComplete: PropTypes.func,
  /** Min height for the collapse animation */
  height: PropTypes.number,
};

export default AnimateHeight;
